import { useEffect, useState } from "react";
import { animate, useMotionValue, useTransform, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { GraduationCap, Building2 } from "lucide-react";
import api from "@/lib/api";

const CountUp = ({ value, testId }) => {
  const mv = useMotionValue(0);
  const text = useTransform(mv, (v) => Math.round(v).toLocaleString("en-IN"));
  useEffect(() => {
    const c = animate(mv, value, { duration: 2, ease: "easeOut" });
    return () => c.stop();
  }, [mv, value]);
  return <motion.span data-testid={testId}>{text}</motion.span>;
};

const Stat = ({ icon: Icon, value, label, tone, testId }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-white/70 bg-white/80 px-5 py-3 shadow-lg shadow-orange-500/10 backdrop-blur">
    <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}><Icon className="h-6 w-6" /></span>
    <div>
      <p className="font-heading text-3xl font-extrabold leading-none text-slate-900 sm:text-4xl">
        <CountUp value={value} testId={testId} />+
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-600">{label}</p>
    </div>
  </div>
);

export const LiveCounter = () => {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => api.get("/public/stats").then(({ data }) => setS(data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  if (!s) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.5 }} className="mt-8" data-testid="live-counter">
      <p className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-rose-600">
        <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-500 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-600" /></span>
        {t("landing.liveLabel")}
      </p>
      <div className="flex flex-wrap gap-4">
        <Stat icon={GraduationCap} value={s.students_placed} label={t("landing.livePlaced")} tone="bg-emerald-100 text-emerald-700" testId="live-placed-count" />
        <Stat icon={Building2} value={s.companies_hiring} label={t("landing.liveCompanies")} tone="bg-sky-100 text-sky-700" testId="live-companies-count" />
      </div>
    </motion.div>
  );
};
